/**
 * SSN (Personnummer) Utility Functions for Frontend
 * 
 * Swedish personnummer standards:
 * - Short format: YYMMDDXXXX (10 digits)
 * - Long format: YYYYMMDDXXXX (12 digits)
 * - Display format: YYMMDD-XXXX (with dash for readability)
 * - Storage format: YYMMDDXXXX (10 digits without dash)
 */

/**
 * Normalize SSN to 10-digit format without dash
 * Accepts: YYMMDDXXXX, YYMMDD-XXXX, YYYYMMDDXXXX, YYYYMMDD-XXXX
 * Returns: YYMMDDXXXX (10 digits)
 * 
 * @param {string} ssn - The SSN to normalize
 * @returns {string} - Normalized 10-digit SSN or original if invalid
 */
export function normalizeSSN(ssn) {
  if (!ssn || typeof ssn !== 'string') {
    return ssn;
  }

  // Remove all non-digit characters (dashes, spaces, etc.)
  const cleanSSN = ssn.replace(/\D/g, '');

  // If already 10 digits, return as is
  if (cleanSSN.length === 10) {
    return cleanSSN;
  }

  // If 12 digits, remove century prefix (first 2 digits)
  if (cleanSSN.length === 12) {
    return cleanSSN.substring(2);
  }

  // If other length, return original (will fail validation)
  return ssn;
}

/**
 * Format SSN for display (adds dash for readability)
 * Input: YYMMDDXXXX (10 digits)
 * Output: YYMMDD-XXXX
 * 
 * @param {string} ssn - The SSN (10 or 12 digits, with or without dash)
 * @returns {string} - Formatted SSN with dash
 */
export function formatSSNForDisplay(ssn) {
  if (!ssn) {
    return '';
  }

  // Normalize first
  const cleanSSN = normalizeSSN(ssn);

  // If not 10 digits after normalization, return as is
  if (cleanSSN.length !== 10) {
    return ssn;
  }

  // Format as YYMMDD-XXXX
  return `${cleanSSN.substring(0, 6)}-${cleanSSN.substring(6)}`;
}

/**
 * Validate SSN format (accepts multiple formats)
 * Accepts:
 * - YYMMDDXXXX (10 digits)
 * - YYMMDD-XXXX (10 digits with dash)
 * - YYYYMMDDXXXX (12 digits)
 * - YYYYMMDD-XXXX (12 digits with dash)
 * 
 * @param {string} ssn - The SSN to validate
 * @returns {boolean} - True if valid format
 */
export function validateSSNFormat(ssn) {
  if (!ssn || typeof ssn !== 'string') {
    return false;
  }

  // Remove all non-digit characters
  const cleanSSN = ssn.replace(/\D/g, '');

  // Must be exactly 10 or 12 digits
  return cleanSSN.length === 10 || cleanSSN.length === 12;
}

/**
 * Format SSN as user types (auto-adds dash after 6 digits)
 * Used for real-time input formatting
 * 
 * @param {string} value - The current input value
 * @returns {string} - Formatted value
 */
export function formatSSNOnInput(value) {
  if (!value) return '';

  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');

  // Limit to 10 digits
  const limited = digitsOnly.substring(0, 10);

  // Add dash after 6 digits
  if (limited.length > 6) {
    return `${limited.substring(0, 6)}-${limited.substring(6)}`;
  }

  return limited;
}

/**
 * Validate SSN date portion (YYMMDD)
 * Checks if the date is valid
 *
 * @param {string} ssn - The SSN to validate
 * @returns {boolean} - True if date is valid
 */
export function validateSSNDate(ssn) {
  if (!ssn) {
    return false;
  }

  // Normalize to 10 digits
  const cleanSSN = normalizeSSN(ssn);

  if (cleanSSN.length !== 10) {
    return false;
  }

  // Extract date components
  const year = parseInt(cleanSSN.substring(0, 2), 10);
  const month = parseInt(cleanSSN.substring(2, 4), 10);
  const day = parseInt(cleanSSN.substring(4, 6), 10);

  // Validate month
  if (month < 1 || month > 12) {
    return false;
  }

  // Validate day - check if it's valid for the given month
  // Note: For coordination numbers (samordningsnummer), 60 is added to the day
  // So day can be 01-31 (normal) or 61-91 (coordination number)
  const isCoordinationNumber = day > 60;
  const actualDay = isCoordinationNumber ? day - 60 : day;

  if (actualDay < 1 || actualDay > 31) {
    return false;
  }

  // Check days per month (simplified - doesn't account for leap years in YY format)
  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (actualDay > daysInMonth[month - 1]) {
    return false;
  }

  return true;
}

/**
 * Validate SSN checksum (Luhn algorithm)
 * Swedish personnummer uses the Luhn algorithm for the last digit
 *
 * @param {string} ssn - The SSN to validate
 * @returns {boolean} - True if checksum is valid
 */
export function validateSSNChecksum(ssn) {
  if (!ssn) {
    return false;
  }

  // Normalize to 10 digits
  const cleanSSN = normalizeSSN(ssn);

  if (cleanSSN.length !== 10) {
    return false;
  }

  // Apply Luhn algorithm to first 9 digits
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let digit = parseInt(cleanSSN[i], 10);

    // Every second digit from right to left (positions 1, 3, 5, 7 from left) should be doubled
    if (i % 2 === 0) {
      digit *= 2;
      // If result is > 9, subtract 9
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
  }

  // Check digit should make sum divisible by 10
  const checkDigit = parseInt(cleanSSN[9], 10);
  const calculatedCheckDigit = (10 - (sum % 10)) % 10;

  return checkDigit === calculatedCheckDigit;
}

/**
 * Get SSN validation error message
 *
 * @param {string} ssn - The SSN to validate
 * @returns {string|null} - Error message or null if valid
 */
export function getSSNValidationError(ssn) {
  if (!ssn) {
    return 'Personnummer är obligatoriskt';
  }

  if (!validateSSNFormat(ssn)) {
    return 'Personnummer måste vara i format ÅÅMMDD-XXXX';
  }

  if (!validateSSNDate(ssn)) {
    return 'Personnummer innehåller ett ogiltigt datum';
  }

  if (!validateSSNChecksum(ssn)) {
    return 'Ogiltigt personnummer (kontrollsiffran stämmer inte)';
  }

  return null;
}

